/**
 * Detects cognos accounts linked to MULTIPLE distinct components. A component's
 * identity is the token before the first '-' (same rule as `checkDoubleMapped`
 * in components/config_central/cognos_config/index.jsx). The relation is read
 * from each config row's `accuracyComponent` / `cognosOnlyComponent` arrays;
 * there is no all-components mappings fetch because the reverse-GSI has no
 * all-components path.
 *
 * "Double mapped" intentionally differs per page:
 *   - Accuracy Mappings: >1 identity in `accuracyComponent` (GMS excluded).
 *   - Cognos Accounts: >1 identity in `accuracyComponent` (GMS excluded) OR in
 *     `cognosOnlyComponent` (GMS INCLUDED) -- a deliberate asymmetry preserving
 *     long-standing page behaviour.
 */

// FRONTEND-ONLY convention carried over from the Cognos Accounts page; the
// backend (MappingsAccessor.format) has no equivalent GMS exclusion.
const EXCLUDED_COMPONENT = 'GMS';

/**
 * Component identity: the token before the first '-' ("COMPA-sub1" -> "COMPA").
 *
 * @param {string} componentKey raw component key
 * @returns {string} component identity, or '' when the key is not a string
 */
export const componentIdentity = (componentKey) =>
  typeof componentKey === 'string' ? componentKey.split('-')[0] : '';

/**
 * Distinct component identities in `componentKeys`; GMS is skipped unless
 * `excludeGms` is false. Non-array input yields an empty Set.
 *
 * @param {Array<string>} componentKeys raw component keys (may be undefined/null)
 * @param {{ excludeGms?: boolean }} [options] excludeGms defaults to true
 * @returns {Set<string>} distinct component identities
 */
const distinctComponentIdentities = (componentKeys, { excludeGms = true } = {}) => {
  const identities = new Set();
  if (!Array.isArray(componentKeys)) {
    return identities;
  }
  for (const componentKey of componentKeys) {
    const identity = componentIdentity(componentKey);
    if (!identity) {
      continue;
    }
    if (excludeGms && identity === EXCLUDED_COMPONENT) {
      continue;
    }
    identities.add(identity);
  }
  return identities;
};

/**
 * Index of cognos account id (as a STRING) -> distinct `accuracyComponent`
 * identities (GMS excluded).
 *
 * @param {Array} cognosAccounts cognos-account rows ({ id, accuracyComponent })
 * @returns {Map<string, Set<string>>} cognos id -> set of component identities
 */
export const buildCognosComponentIndex = (cognosAccounts) => {
  const index = new Map();
  if (!Array.isArray(cognosAccounts)) {
    return index;
  }
  for (const account of cognosAccounts) {
    if (!account || account.id === null || account.id === undefined) {
      continue;
    }
    // Key by String(id): ids arrive as numbers from JSON.parse but are probed with
    // the Select's string selectedId, and Map keys do not coerce.
    index.set(String(account.id), distinctComponentIdentities(account.accuracyComponent));
  }
  return index;
};

/**
 * True when `componentKeys` references more than one distinct identity.
 * `excludeGms` defaults to true; pass false to count GMS. The 'GMS' match is
 * case-sensitive.
 *
 * @param {Array<string>} componentKeys raw component keys (may be undefined/null)
 * @param {{ excludeGms?: boolean }} [options] excludeGms defaults to true
 * @returns {boolean} whether >1 distinct identity is referenced
 */
export const hasMultipleComponentIdentities = (componentKeys, options) =>
  distinctComponentIdentities(componentKeys, options).size > 1;

/**
 * Derives the set of cognos account ids that are linked to more than one
 * distinct component, from an index produced by buildCognosComponentIndex.
 *
 * @param {Map<string, Set<string>>} index reverse index (cognos id -> components)
 * @returns {Set<string>} cognos ids linked to > 1 distinct component
 */
export const getMultiComponentCognosIds = (index) => {
  const ids = new Set();
  if (!index) {
    return ids;
  }
  for (const [cognosId, components] of index) {
    if (components.size > 1) {
      ids.add(cognosId);
    }
  }
  return ids;
};



// WEBPACK FOOTER //
// ./src/utils/cognosMultiComponent.js